// src/pages/produtos/produtos/ProdutoSearchMultiplos.js
import React, { useRef, useState } from 'react';
import { FaCheck, FaFilter, FaPlus, FaSearch, FaShoppingBasket, FaStar, FaSortAmountDown, FaTrash } from 'react-icons/fa';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import { ProdutoSelecaoMultiplosProvider, useProdutoSelecaoMultiplos } from '../../../contexts/produtos/ProdutoSelecaoMultiplosContext';
import { normalizarTextoProduto, produtoCorrespondeFiltro } from '../../../services/produtos/ProdutoFiltroService';
import { formatNumeric, parseNumeric } from '../../../utils/numberUtils';
import Input from '../../../components/input/Input';
import InputCheck from '../../../components/input/InputCheck';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalTable from '../../../components/modalForm/ModalTable';
import LazyLoading from '../../../components/modalForm/LazyLoading';
import ModalSearchBar from '../../../components/modalForm/ModalSearchBar';
import { ModalActionButton } from '../../../components/modalForm/ModalActions';
import ModalQuantityControl from '../../../components/modalForm/ModalQuantityControl';
import ProdutoSearch from './ProdutoSearch';
import './ProdutoSearchMultiplos.css';

const GRUPOS_OCULTOS_KEY = 'produtoSearchMultiplos_hiddenGroupIds';
const lerGruposOcultos = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(GRUPOS_OCULTOS_KEY) || '[]');
    return Array.isArray(ids) ? ids : [];
  } catch { return []; }
};

const ProdutoSearchMultiplosConteudo = ({ onHide, onSelectProdutos }) => {
  const { produtos = [], produtosFavoritos = [], alteraFavorito } = useProduto();
  const { itens, alterarQuantidade, unidades, subtotal } = useProdutoSelecaoMultiplos();
  const [busca, setBusca] = useState('');
  const [buscaGrupo, setBuscaGrupo] = useState('');
  const [grupoId, setGrupoId] = useState(null);
  const [favoritos, setFavoritos] = useState(false);
  const [inativos, setInativos] = useState(false);
  const [ordem, setOrdem] = useState({ campo: 'id', crescente: true });
  const [ocultos, setOcultos] = useState(lerGruposOcultos);
  const [rascunhoGrupos, setRascunhoGrupos] = useState([]);
  const [auxiliar, setAuxiliar] = useState(null);
  const [adicionandoFavorito, setAdicionandoFavorito] = useState(false);
  const [fechando, setFechando] = useState(false);
  const [erro, setErro] = useState('');
  const fechamentoRef = useRef(false);
  const listaRef = useRef(null);
  const buscaRef = useRef(null);

  const grupos = Array.from(new Map(produtos.filter(produto => produto.grupo &&
    (inativos || (!produto.inativo && !produto.grupo.inativo)))
    .map(produto => [produto.grupo.id, produto.grupo])).values())
    .sort((a, b) => normalizarTextoProduto(a.descricao).localeCompare(normalizarTextoProduto(b.descricao)));
  const gruposFiltrados = grupos.filter(grupo => normalizarTextoProduto(grupo.descricao).includes(normalizarTextoProduto(buscaGrupo)));
  const resultados = (favoritos ? produtosFavoritos : produtos).filter(produto =>
    (produtoCorrespondeFiltro(produto.descricao, busca) || String(produto.id).includes(busca.trim())) &&
    (inativos || (!produto.inativo && !produto.grupo?.inativo)) &&
    (favoritos || (grupoId !== null ? produto.grupo?.id === grupoId : !ocultos.includes(produto.grupo?.id))))
    .sort((a, b) => {
      const comparacao = ordem.campo === 'id' ? a.id - b.id : normalizarTextoProduto(a.descricao).localeCompare(normalizarTextoProduto(b.descricao));
      return ordem.crescente ? comparacao : -comparacao;
    });

  const cancelarSelecao = () => {
    if (fechamentoRef.current) return;
    fechamentoRef.current = true;
    onHide();
  };

  const confirmarSelecao = async () => {
    if (fechamentoRef.current || !itens.length) return;
    const itemSemValor = itens.find(item => parseNumeric(item.produto?.preco) <= 0);
    if (itemSemValor) {
      setErro(`O produto ${itemSemValor.produto.descricao} não pode ser adicionado com valor zerado.`);
      return;
    }
    fechamentoRef.current = true;
    setFechando(true);
    setErro('');
    try {
      const resultado = await onSelectProdutos(itens);
      if (resultado === false) {
        fechamentoRef.current = false;
        setFechando(false);
        return;
      }
    } catch {
      setErro('Não foi possível incluir os produtos. Tente novamente.');
      fechamentoRef.current = false;
      setFechando(false);
      return;
    }
    onHide();
  };

  const navegarProduto = event => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const botoes = Array.from(listaRef.current?.querySelectorAll('[data-adicionar-produto]:not(:disabled)') || []);
    const index = botoes.indexOf(event.currentTarget);
    const proximo = botoes[Math.max(0, Math.min(botoes.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))];
    proximo?.focus();
    proximo?.scrollIntoView?.({ block: 'nearest' });
  };
  const ordenar = campo => setOrdem(atual => ({ campo, crescente: atual.campo === campo ? !atual.crescente : true }));
  const salvarGrupos = () => {
    setOcultos(rascunhoGrupos);
    setGrupoId(null);
    try { localStorage.setItem(GRUPOS_OCULTOS_KEY, JSON.stringify(rascunhoGrupos)); } catch { /* O filtro continua funcionando nesta sessão. */ }
    setAuxiliar(null);
  };
  const tentarAlterarQuantidade = (produto, incremento) => {
    if (incremento > 0 && parseNumeric(produto.preco) <= 0) {
      setErro(`O produto ${produto.descricao} não pode ser adicionado com valor zerado.`);
      return;
    }
    setErro('');
    alterarQuantidade(produto, incremento);
  };

  return <>
    <ModalContainer show variant="busca" onHide={cancelarSelecao} largura="1100px" contentClassName="produto-multiplos-container produto-multiplos">
        <ModalHeader title="Adicionar produtos" 
        subtitle="Ajuste as quantidades e clique em Adicionar selecionados para confirmar."
        // subtitle="Clique no produto para adicionar. Ajuste a quantidade com − e +." 
        icon={FaShoppingBasket} onHide={cancelarSelecao} closeDisabled={fechando} closeLabel="Cancelar seleção" />
        <ModalBody>
          <fieldset disabled={fechando} className="pm-campos">
            <div className="pm-pesquisa">
              <ModalSearchBar label="Produto" inputRef={buscaRef} autoFocus type="text" size="100%" placeholder="Buscar por descrição ou código..." value={busca} onChange={event => setBusca(event.target.value)} onKeyDown={event => {
                if (event.key === 'Enter') { event.preventDefault(); listaRef.current?.querySelector('[data-adicionar-produto]:not(:disabled)')?.focus(); }
              }} />
              <InputCheck id="pm-inativos" label="Inativos" checked={inativos} onChange={event => setInativos(event.target.checked)} />
            </div>
            <div className="pm-conteudo">
              <aside className="pm-grupos" aria-label="Grupos de produtos">
                <div className="pm-grupos-titulo"><strong>Grupos</strong><button type="button" className="pm-botao-icone" aria-label="Filtrar grupos" onClick={() => { setRascunhoGrupos(ocultos); setAuxiliar('grupos'); }}><FaFilter /></button></div>
                <Input label="Grupo" type="text" size="100%" placeholder="Filtrar grupos..." value={buscaGrupo} onChange={event => setBuscaGrupo(event.target.value)} />
                <nav className="pm-lista-grupos">
                  <button type="button" aria-pressed={!favoritos && grupoId === null} onClick={() => { setFavoritos(false); setGrupoId(null); }}>Todos os produtos</button>
                  <button type="button" aria-pressed={favoritos} onClick={() => { setFavoritos(true); setGrupoId(null); }}><FaStar /> Favoritos</button>
                  {gruposFiltrados.filter(grupo => !ocultos.includes(grupo.id)).map(grupo => <button key={grupo.id} type="button" aria-pressed={!favoritos && grupoId === grupo.id} onClick={() => { setFavoritos(false); setGrupoId(grupoId === grupo.id ? null : grupo.id); }}>{grupo.descricao}</button>)}
                </nav>
                {ocultos.length > 0 && <small>{ocultos.length} grupo(s) oculto(s)</small>}
              </aside>
              <section className="pm-produtos" ref={listaRef} aria-label="Produtos disponíveis">
                {favoritos && <div className="pm-resultados"><button type="button" className="pm-link" onClick={() => setAuxiliar('favoritos')}>Gerenciar favoritos</button></div>}
                <LazyLoading items={resultados} resetKey={JSON.stringify([busca, grupoId, favoritos, inativos, ocultos, ordem])}>
                  {({ containerRef, handleScroll, visibleItems }) => <ModalTable containerRef={containerRef} onContainerScroll={handleScroll} containerStyle={{ maxHeight: 'min(52dvh, 520px)', overflow: 'auto' }}>
                    <thead><tr>
                      <th aria-sort={ordem.campo === 'id' ? (ordem.crescente ? 'ascending' : 'descending') : 'none'}><button type="button" onClick={() => ordenar('id')}>Cód. <FaSortAmountDown /></button></th>
                      <th aria-sort={ordem.campo === 'descricao' ? (ordem.crescente ? 'ascending' : 'descending') : 'none'}><button type="button" onClick={() => ordenar('descricao')}>Produto <FaSortAmountDown /></button></th>
                      <th className="pm-preco">Preço</th><th className="pm-quantidade">Quantidade</th>
                    </tr></thead>
                    <tbody>{visibleItems.map(produto => {
                      const quantidade = itens.find(item => item.produto.id === produto.id)?.quantidade || 0;
                      const precoInvalido = parseNumeric(produto.preco) <= 0;
                      return <tr key={produto.id} className={`${quantidade ? 'pm-selecionado' : ''} ${precoInvalido ? 'pm-preco-invalido' : ''}`} onClick={() => !fechando && !quantidade && tentarAlterarQuantidade(produto, 1)} onDoubleClick={() => !fechando && confirmarSelecao()}>
                        <td className="pm-codigo">{produto.id}</td>
                        <td><span className="pm-descricao">{produto.descricao}</span>{quantidade > 0 && <small className="pm-adicionado"><FaCheck /> Selecionado</small>}</td>
                        <td className="pm-preco">R$ {formatNumeric(produto.preco)}{precoInvalido && <small>Valor obrigatório</small>}</td>
                        <td><ModalQuantityControl className="pm-contador" name={produto.descricao} value={quantidade}
                          decreaseDisabled={!quantidade} increaseDisabled={precoInvalido}
                          onDecrease={() => tentarAlterarQuantidade(produto, -1)} onIncrease={() => tentarAlterarQuantidade(produto, 1)}
                          increaseProps={{ 'data-adicionar-produto': true, title: precoInvalido ? 'Informe um valor maior que zero para adicionar' : undefined, onKeyDown: navegarProduto }} /></td>
                      </tr>;
                    })}</tbody>
                  </ModalTable>}
                </LazyLoading>
                {!resultados.length && <div className="pm-vazio"><FaSearch /><strong>Nenhum produto encontrado</strong><span>Tente outra descrição ou ajuste os filtros.</span></div>}
              </section>
            </div>
          </fieldset>
          <div className="pm-resumo" aria-live="polite"><span><FaShoppingBasket /> <strong>{unidades}</strong> unidade(s) em {itens.length} produto(s)</span><span>Subtotal <strong>R$ {formatNumeric(subtotal)}</strong></span></div>
          {/* <p className="pm-ajuda">Os produtos serão incluídos na venda ao fechar esta janela.</p> */}
          {erro && <p role="alert" className="text-danger">{erro}</p>}
        </ModalBody>
        <ModalFooter>
          <ModalActionButton action="close" disabled={fechando} onClick={cancelarSelecao}>Cancelar</ModalActionButton>
          <ModalActionButton action="confirm" className="pm-fechar" disabled={fechando || !itens.length} aria-busy={fechando} onClick={confirmarSelecao}>{fechando ? 'Incluindo...' : 'Adicionar selecionados'}</ModalActionButton>
        </ModalFooter>
    </ModalContainer>
    <ModalContainer show={Boolean(auxiliar)} variant="cadastro" onHide={() => setAuxiliar(null)} largura="540px" contentClassName="produto-multiplos-container produto-multiplos">
        <ModalHeader title={auxiliar === 'grupos' ? 'Filtrar grupos' : 'Produtos favoritos'} onHide={() => setAuxiliar(null)} closeLabel="Fechar janela auxiliar" icon={auxiliar === 'grupos' ? FaFilter : FaStar} />
        <ModalBody>
          {auxiliar === 'grupos' ? <>
            <div className="pm-acoes"><button type="button" className="pm-link" onClick={() => setRascunhoGrupos([])}>Marcar todos</button><button type="button" className="pm-link" onClick={() => setRascunhoGrupos(grupos.map(grupo => grupo.id))}>Desmarcar todos</button></div>
            <div className="pm-auxiliar-lista">{grupos.map(grupo => <InputCheck key={grupo.id} id={`pm-grupo-${grupo.id}`} label={grupo.descricao} checked={!rascunhoGrupos.includes(grupo.id)} onChange={() => setRascunhoGrupos(atual => atual.includes(grupo.id) ? atual.filter(id => id !== grupo.id) : [...atual, grupo.id])} />)}</div>
          </> : <>
            <button type="button" className="btn btn-outline-primary mb-3" onClick={() => setAdicionandoFavorito(true)}><FaPlus /> Adicionar favorito</button>
            <div className="pm-auxiliar-lista">{produtosFavoritos.map(produto => <div className="pm-favorito" key={produto.id}><span>{produto.descricao}</span><button type="button" className="pm-botao-icone" aria-label={`Remover favorito ${produto.descricao}`} onClick={() => alteraFavorito(produto.id, false)}><FaTrash /></button></div>)}{!produtosFavoritos.length && <p>Nenhum favorito cadastrado.</p>}</div>
          </>}
        </ModalBody>
        <ModalFooter><ModalActionButton action={auxiliar === 'grupos' ? 'apply' : 'close'} onClick={auxiliar === 'grupos' ? salvarGrupos : () => setAuxiliar(null)}>{auxiliar === 'grupos' ? 'Aplicar' : 'Fechar'}</ModalActionButton></ModalFooter>
    </ModalContainer>
    <ProdutoSearch show={adicionandoFavorito} onHide={() => setAdicionandoFavorito(false)} onSelectProduto={async produto => { await alteraFavorito(produto.id, true); setAdicionandoFavorito(false); }} />
  </>;
};

const ProdutoSearchMultiplos = ({ show, onHide, onSelectProdutos }) => show ?
  <ProdutoSelecaoMultiplosProvider><ProdutoSearchMultiplosConteudo onHide={onHide} onSelectProdutos={onSelectProdutos} /></ProdutoSelecaoMultiplosProvider> : null;

export default ProdutoSearchMultiplos;
